import { lazy, Suspense } from 'react'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { Modal } from '@/components/common'
import type { Invoice } from '@/types/invoice'
import type { InvoiceDetailController } from './useInvoiceDetail'
import InvoiceActionBar from './InvoiceActionBar'
import InvoiceAmountsPanel from './InvoiceAmountsPanel'
import InvoiceExtrasAccordion from './InvoiceExtrasAccordion'
import InvoiceFactsPanel from './InvoiceFactsPanel'
import InvoiceHeaderBar from './InvoiceHeaderBar'
import InvoiceRemindersPanel from './InvoiceRemindersPanel'
import { InlineField } from '@/components/common'
import { fileUrl, isPdfFile } from './invoiceUtils'

// react-pdf + pdfjs sind ~350 kB und werden NUR hier gebraucht — nachladen, damit
// das Hauptbundle fuer alle anderen Seiten schlank bleibt.
const FilePreviewPane = lazy(() => import('@/components/common/FilePreviewPane'))

interface Props {
  detail: InvoiceDetailController
  /** Oeffnet den Zuordnungs-Dialog fuer eine Mahnung (liegt auf Seitenebene). */
  onAssign: (invoice: Invoice) => void
}

/**
 * Fullscreen-Detailansicht: links der Beleg, rechts die Daten.
 *
 * Kopf- und Fussleiste sind gepinnt (grid-rows auto/1fr/auto), es scrollt nur die
 * Mitte — und dort jede Spalte fuer sich. Unter 1280 px stapelt das Layout, sonst
 * waere die Datenspalte auf Notebooks unbrauchbar schmal.
 */
export default function InvoiceDetailModal({ detail, onAssign }: Props) {
  const { invoice, loading, credits, openSum, stack, parentLabel } = detail
  const isOpen = stack.length > 0

  return (
    <Modal
      isOpen={isOpen}
      onClose={detail.close}
      size="screen"
      className="flex flex-col"
      // Der Body traegt Inhalt UND Aktionsleiste als Flex-Spalte: Modal rendert alle
      // children in den Body, ein Geschwister-Element waere sonst nicht moeglich.
      bodyClassName="flex min-h-0 flex-1 flex-col"
      headerContent={
        invoice ? (
          <InvoiceHeaderBar
            invoice={invoice}
            parentLabel={parentLabel}
            openSum={openSum}
            onBack={detail.back}
            onClose={detail.close}
            onToggleImportant={detail.toggleImportant}
          />
        ) : (
          <div className="flex flex-shrink-0 items-center border-b border-gray-200 bg-white px-4 py-3 text-sm text-gray-400">
            Rechnung wird geladen…
          </div>
        )
      }
    >
      {!invoice ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : (
        <div className="grid min-h-0 flex-1 grid-rows-[minmax(340px,55vh)_auto] overflow-y-auto xl:grid-cols-[60fr_40fr] xl:grid-rows-1 xl:overflow-hidden">
          {/* Beleg */}
          <div className="min-h-0 min-w-0 border-b border-gray-200 xl:border-b-0 xl:border-r">
            <Suspense fallback={
              <div className="flex h-full items-center justify-center bg-gray-100">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            }>
              <FilePreviewPane
                url={fileUrl(invoice.original_file_path)}
                fileName={invoice.original_file_name}
                isPdf={isPdfFile(invoice)}
              />
            </Suspense>
          </div>

          {/* Daten — scrollt auf breiten Schirmen fuer sich, gestapelt mit der Seite */}
          <div className="relative min-h-0 space-y-2.5 bg-gray-50 p-3 xl:overflow-y-auto">
            {loading && (
              <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1 shadow">
                <Loader2 className="h-4 w-4 animate-spin text-primary-500" />
              </div>
            )}

            {invoice.clarification_note && (
              <p className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs text-orange-800">
                <strong>Klärung:</strong> {invoice.clarification_note}
              </p>
            )}
            {invoice.rejection_reason && (
              <p className="flex gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span><strong>Abgelehnt:</strong> {invoice.rejection_reason}</span>
              </p>
            )}

            <InvoiceFactsPanel invoice={invoice} onSave={detail.updateField} />
            <InvoiceAmountsPanel invoice={invoice} applied={credits.applied} onSave={detail.updateField} />

            <section className="rounded-lg border border-gray-200 bg-white">
              <InlineField
                label="Beschreibung" field="description" value={invoice.description}
                type="textarea" onSave={detail.updateField}
              />
            </section>

            <InvoiceRemindersPanel
              invoice={invoice}
              onOpen={detail.push}
              onAssign={() => onAssign(invoice)}
              onFeesChanged={detail.reload}
              onMarkPaid={detail.markLinkedPaid}
              markPaidPending={detail.pending.markPaid}
            />

            <InvoiceExtrasAccordion
              invoice={invoice}
              credits={credits}
              onSave={detail.updateField}
              onApplyCredits={detail.applyCredits}
            />
          </div>
        </div>
      )}

      {invoice && (
        <InvoiceActionBar
          invoice={invoice}
          openSum={openSum}
          onStatus={detail.setStatus}
          onPay={detail.pay}
          onDelete={detail.remove}
          pending={detail.pending}
        />
      )}
    </Modal>
  )
}
